import React, { useState } from "react";
import { Button, Modal, Nav, Badge, Card, Row, Col } from "react-bootstrap";
import { useAuth } from "../../hooks/useAuth";
import PartnerCustomer from "./PartnerCustomers";
import { 
  FaBuilding, FaGlobe, FaEnvelope, FaMapMarkerAlt, FaCity, 
  FaFlag, FaUser, FaPhone, FaBullseye, FaIndustry, 
  FaHandshake, FaChartLine, FaQuestion, FaCalendarAlt, 
  FaFileAlt, FaCheckCircle, FaTimesCircle, FaDownload, FaEye
} from "react-icons/fa";

const PartnerDetails = ({ data, setShow }) => {
  const [activeKey, setActiveKey] = useState("basicDetails");
  
  const parseArray = (value) => {
    try {
      const list = JSON.parse(value);
      return Array.isArray(list) ? list.filter(Boolean) : [];
    } catch {
      return [];
    }
  };

  const renderField = (icon, label, value, color = "inherit") => {
    return value ? (
      <div className="d-flex align-items-start mb-3">
        <div className="me-2 text-primary" style={{ minWidth: "24px" }}>{icon}</div>
        <div>
          <strong>{label}:</strong> <span style={{ color }}>{value}</span>
        </div>
      </div>
    ) : null;
  };

  const renderList = (icon, label, rawValue) => {
    const items = parseArray(rawValue);
    return items.length ? (
      <div className="d-flex align-items-start mb-3">
        <div className="me-2 text-primary" style={{ minWidth: "24px" }}>{icon}</div>
        <div style={{ flex: 1 }}>
          <strong>{label}:</strong>
          <div className="d-flex flex-wrap mt-2">
            {items.map((item, index) => (
              <Badge 
                key={index} 
                bg="#fff" 
                text="dark" 
                className="me-2 mb-2 p-2 text-capitalize"
                style={{ border: "1px solid rgb(69, 141, 249)", color: "gray",  borderLeft: '3px solid rgb(69, 141, 249)' }}
              >
                {item.replace(/_/g, " ")}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    ) : null;
  };

  const getPartnerTypeColor = (type) => {
    const types = {
      tech: "#4285F4",
      sales: "#FBBC05",
      default: "#6c757d"
    };
    return types[type] || types.default;
  };

  return (
    <Modal
      onHide={() => setShow(false)}
      show={true}
      size="xl"
      dialogClassName="full-width partnership-details"
      backdropClassName={`global-backdrop`}
      centered={true}
      animation={false}
      enforceFocus={true}
      backdrop="static"
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="w-100">
          <div className="d-flex heading justify-content-between align-items-center w-95">
            <div>
              <h2 className="mb-0">Partner Details</h2>
              <div className="d-flex align-items-center mt-2">
                <FaBuilding className="text-primary me-2" />
                <h4 className="mb-0">{data.company_name}</h4>
                {data.partner_type && (
                  <Badge 
                    pill 
                    className="ms-3" 
                    style={{ 
                      backgroundColor: getPartnerTypeColor(data.partner_type),
                      textTransform: 'capitalize'
                    }}
                  >
                    {data.partner_type}
                  </Badge>
                )}
              </div>
            </div>
            <div>
              <Badge 
                pill 
                bg={data.is_verified ? "success" : "warning"} 
                className="px-3 py-2"
              >
                {data.is_verified ? 
                  <><FaCheckCircle className="me-1" /> Verified</> : 
                  <><FaTimesCircle className="me-1" /> Pending Verification</>
                }
              </Badge>
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2">
        <Nav
          variant="tabs"
          defaultActiveKey="basicDetails"
          onSelect={(key) => setActiveKey(key)}
          className="mb-4"
        >
          <Nav.Item>
            <Nav.Link eventKey="basicDetails" className="d-flex align-items-center">
              <FaUser className="me-2" /> Basic Information
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="customers" className="d-flex align-items-center">
              <FaHandshake className="me-2" /> Customers
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="documents" className="d-flex align-items-center">
              <FaFileAlt className="me-2" /> Documents
            </Nav.Link>
          </Nav.Item>
        </Nav>
        
        {activeKey === "basicDetails" && (
          <div className="container-fluid py-2">
            <Row>
              {/* Company Info Card */}
              <Col md={6} className="mb-4">
                <Card className="h-100 shadow-sm border-0">
                  <Card.Header className="bg-primaries text-white">
                    <h5 className="mb-0"><FaBuilding className="me-2" /> Company Information</h5>
                  </Card.Header>
                  <Card.Body>
                    {renderField(<FaBuilding />, "Company ID", data.companyID)}
                    {renderField(<FaGlobe />, "Website", 
                      <a href={data.company_website.startsWith('http') ? data.company_website : `https://${data.company_website}`} 
                         target="_blank" rel="noopener noreferrer" className="text-primary">
                        {data.company_website}
                      </a>
                    )}
                    {renderField(<FaEnvelope />, "Email", 
                      <a href={`mailto:${data.company_email}`} className="text-primary">
                        {data.company_email}
                      </a>
                    )}
                    
                    <div className="mt-4">
                      <h6 className="border-bottom pb-2"><FaMapMarkerAlt className="me-2" /> Address</h6>
                      {renderField(null, "Address Line 1", data.address_line_1)}
                      {renderField(null, "Address Line 2", data.address_line_2)}
                      {renderField(<FaCity />, "City", data.city)}
                      {renderField(null, "State", data.state)}
                      {renderField(<FaFlag />, "Country", data.country)}
                      {renderField(null, "ZIP Code", data.zip_code)}
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Contact Info Card */}
              <Col md={6} className="mb-4">
                <Card className="h-100 shadow-sm border-0">
                  <Card.Header className="bg-primaries text-white">
                    <h5 className="mb-0"><FaUser className="me-2" /> Contact Person</h5>
                  </Card.Header>
                  <Card.Body>
                    {renderField(<FaUser />, "Title", data.title)}
                    {renderField(null, "Full Name", `${data.first_name} ${data.last_name}`)}
                    {renderField(<FaEnvelope />, "Personal Email", 
                      <a href={`mailto:${data.personal_email}`} className="text-primary">
                        {data.personal_email}
                      </a>
                    )}
                    {renderField(<FaPhone />, "Phone", `${data.country_code} ${data.phone_line}`)}
                    
                    <div className="mt-4">
                      <h6 className="border-bottom pb-2"><FaCalendarAlt className="me-2" /> Registration Info</h6>
                      {renderField(<FaCalendarAlt />, "Created At", new Date(data.createdAt).toLocaleString())}
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Business Info Card */}
              <Col md={12} className="mb-4">
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-primaries text-white">
                    <h5 className="mb-0"><FaChartLine className="me-2" /> Business Information</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        {renderList(<FaBullseye />, "Core Focus", data.core_focus)}
                        {renderList(<FaHandshake />, "Solutions", data.solution)}
                        {renderList(<FaIndustry />, "Industries", data.industry)}
                      </Col>
                      <Col md={6}>
                        {renderField(<FaBuilding />, "Business Size", data.business_size)}
                        {renderField(<FaChartLine />, "Annual Revenue", data.annual_revenue?.replace(/_/g, " - "))}
                        {renderField(<FaQuestion />, "Why Interested", data.why_interested)}
                        {renderList(<FaHandshake />, "Resell Partners", data.resell)}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        )}
        
        {activeKey === "customers" && (
          <div className="container-fluid py-2 partner-customer">
            <PartnerCustomer partnerId={data.id} />
          </div>
        )}
        
        {activeKey === "documents" && (
          <div className="container-fluid py-2">
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-primaries text-white">
                <h5 className="mb-0"><FaFileAlt className="me-2" /> Uploaded Documents</h5>
              </Card.Header>
              <Card.Body>
                <DocumentsSection data={data} />
              </Card.Body>
            </Card>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default PartnerDetails;

const DocumentsSection = ({ data }) => {
  const { backendUrl } = useAuth();
  
  if (!data.documents) {
    return <p className="text-muted">No documents uploaded.</p>;
  } else {
    if (!Array.isArray(data.documents))
      data.documents = JSON.parse(data.documents);
    if (!data.documents.length) {
      return <p className="text-muted">No documents uploaded.</p>;
    }
  }

  // Group documents by type
  const getDocumentType = (path) => {
    const extension = path.split('.').pop().toLowerCase();
    if (['pdf'].includes(extension)) return 'pdf';
    if (['doc', 'docx', 'odt'].includes(extension)) return 'word';
    if (['xls', 'xlsx', 'ods'].includes(extension)) return 'excel';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(extension)) return 'image';
    return 'other';
  };

  const getDocumentIcon = (type) => {
    switch (type) {
      case 'pdf': return <FaFileAlt className="text-danger" />;
      case 'word': return <FaFileAlt className="text-primary" />;
      case 'excel': return <FaFileAlt className="text-success" />;
      case 'image': return <FaFileAlt className="text-info" />;
      default: return <FaFileAlt className="text-secondary" />;
    }
  };

  return (
    <Row>
      {data.documents.map((doc, index) => (
        <Col md={4} key={index} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <div className="me-3 fs-3">
                  {getDocumentIcon(getDocumentType(doc.path || ''))}
                </div>
                <div>
                  <h6 className="mb-1">{doc.name || `Document ${index + 1}`}</h6>
                  <small className="text-muted">
                    {doc.path ? doc.path.split('.').pop().toUpperCase() : 'Unknown'} File
                  </small>
                </div>
              </div>
              
              {doc.path && (
                <div className="text-end">
                  <Button
                    variant={["xls", "xlsx", "ods", "odt", "docx"].includes(
                      doc.path.split(".").pop()
                    ) ? "outline-primary" : "outline-info"}
                    size="sm"
                    onClick={() => {
                      window.open(
                        `${backendUrl}/api/partnership/partner/view-document/${
                          data.id
                        }/${doc.id}`,
                        "_blank"
                      );
                    }}
                  >
                    {["xls", "xlsx", "ods", "odt", "docx"].includes(
                      doc.path.split(".").pop()
                    ) ? (
                      <><FaDownload className="me-1" /> Download</>
                    ) : (
                      <><FaEye className="me-1" /> View</>
                    )}
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );
};
